import { Injectable, inject } from '@angular/core';

import { FormBuilder, Validators } from '@angular/forms';

import {
  CropAid,
  FertilizerData,
  NutritionalCompositionData,
  UnitOfMeasurementEnum,
  ProprietaryCustomerInformationData,
  ProprietaryFertilizerData,
  UpsertProprietaryFertilizerData,
} from '../models/proprietary-fertilizer.model';

@Injectable({
  providedIn: 'root',
})
export class ProprietaryFertilizerFormService {
  private readonly fb = inject(FormBuilder);

  createEmptyNutritionalCompositionData(): NutritionalCompositionData {
    return {
      n: null,
      p2O5: null,
      k2O: null,
      na2O: null,
      mgO: null,
      caO: null,
      sO3: null,
      b: null,
      mn: null,
      fe: null,
      zn: null,
      cu: null,
      co: null,
    };
  }

  createEmptyProprietaryCustomerInformationData(): ProprietaryCustomerInformationData {
    return {
      cropYear: null,
      originalCropAidId: null,
      expirationCropYear: null,
    };
  }

  createEmptyProprietaryFertilizer(): ProprietaryFertilizerData {
    return {
      id: -1,
      originalCropAidId: null,
      name: '',
      nutritionalComposition: this.createEmptyNutritionalCompositionData(),
    };
  }

  createEmptyProprietaryFertilizerFromCropAid(cropAid: CropAid): ProprietaryFertilizerData {
    return {
      id: cropAid.id,
      originalCropAidId: cropAid.fertilizer?.proprietaryCustomerInformation?.originalCropAidId,
      name: cropAid.name,
      expirationCropYear: cropAid.fertilizer?.proprietaryCustomerInformation?.expirationCropYear,
      nutritionalComposition: cropAid.fertilizer?.nutritionalComposition,
    };
  }

  createProprietaryFertilizerData(proprietaryFertilizer: CropAid): ProprietaryFertilizerData {
    return {
      id: proprietaryFertilizer.id,
      originalCropAidId:
        proprietaryFertilizer.fertilizer?.proprietaryCustomerInformation?.originalCropAidId,
      name: proprietaryFertilizer.name,
      nutritionalComposition: proprietaryFertilizer.fertilizer?.nutritionalComposition,
    };
  }

  createNutritionalCompositionFormGroup(
    nutritionalComposition: Partial<NutritionalCompositionData> = this.createEmptyNutritionalCompositionData(),
  ) {
    return this.fb.group({
      n: [nutritionalComposition?.n, [Validators.min(0), Validators.max(100)]],
      p2O5: [nutritionalComposition?.p2O5, [Validators.min(0), Validators.max(100)]],
      k2O: [nutritionalComposition?.k2O, [Validators.min(0), Validators.max(100)]],
      na2O: [nutritionalComposition?.na2O, [Validators.min(0), Validators.max(100)]],
      mgO: [nutritionalComposition?.mgO, [Validators.min(0), Validators.max(100)]],
      caO: [nutritionalComposition?.caO, [Validators.min(0), Validators.max(100)]],
      sO3: [nutritionalComposition?.sO3, [Validators.min(0), Validators.max(100)]],
      b: [nutritionalComposition?.b, [Validators.min(0), Validators.max(100)]],
      mn: [nutritionalComposition?.mn, [Validators.min(0), Validators.max(100)]],
      fe: [nutritionalComposition?.fe, [Validators.min(0), Validators.max(100)]],
      zn: [nutritionalComposition?.zn, [Validators.min(0), Validators.max(100)]],
      cu: [nutritionalComposition?.cu, [Validators.min(0), Validators.max(100)]],
      co: [nutritionalComposition?.co, [Validators.min(0), Validators.max(100)]],
    });
  }

  getUnitOfMeasurementMultiplier(unitOfMeasurement: UnitOfMeasurementEnum, density: number) {
    switch (unitOfMeasurement) {
      case UnitOfMeasurementEnum.G:
        return 0.001;
      case UnitOfMeasurementEnum.KG:
        return 1;
      case UnitOfMeasurementEnum.T:
        return 1000;
      case UnitOfMeasurementEnum.ML:
        return 0.001 * density;
      case UnitOfMeasurementEnum.L:
        return 1 * density;
      case UnitOfMeasurementEnum.ST:
        return 1;
    }
  }

  getUnitOfMeasurementDecimalPlaces(unitOfMeasurement: UnitOfMeasurementEnum) {
    switch (unitOfMeasurement) {
      case UnitOfMeasurementEnum.G:
        return 8;
      case UnitOfMeasurementEnum.KG:
        return 5;
      case UnitOfMeasurementEnum.T:
        return 2;
      case UnitOfMeasurementEnum.ML:
        return 8;
      case UnitOfMeasurementEnum.L:
        return 5;
      case UnitOfMeasurementEnum.ST:
        return 5;
    }
  }

  roundUnitOfMeasurementValue(value: number, unitOfMeasurement: UnitOfMeasurementEnum) {
    const decimals = this.getUnitOfMeasurementDecimalPlaces(unitOfMeasurement);
    const input = Number(value.toFixed(20) + 'e' + decimals);
    return Number(Math.round(input) + 'e-' + decimals);
  }

  getProprietaryFertilizerUpsertData(
    proprietaryFertilizer: ProprietaryFertilizerData,
    cropYear: number,
    unitOfMeasurement: UnitOfMeasurementEnum,
    density: number,
  ): UpsertProprietaryFertilizerData {
    return {
      id: proprietaryFertilizer.id,
      name: proprietaryFertilizer.name,
      nutritionalComposition: Object.keys(proprietaryFertilizer.nutritionalComposition).reduce(
        (nutritionalComposition, nutrient: keyof NutritionalCompositionData) => ({
          ...nutritionalComposition,
          [nutrient]: this.getNutritionalCompositionUpsertValue(
            proprietaryFertilizer.nutritionalComposition[nutrient],
            unitOfMeasurement,
            density,
          ),
        }),
        this.createEmptyNutritionalCompositionData(),
      ),
      proprietaryCustomerInformation: {
        originalCropAidId: proprietaryFertilizer.originalCropAidId,
        cropYear,
        expirationCropYear: proprietaryFertilizer.expirationCropYear,
      },
    };
  }

  getNutritionalCompositionUpsertValue(
    value: number,
    unitOfMeasurement: UnitOfMeasurementEnum,
    density: number,
  ) {
    const convertedValue = value
      ? this.roundUnitOfMeasurementValue(
          value * this.getUnitOfMeasurementMultiplier(unitOfMeasurement, density) * 0.01,
          unitOfMeasurement,
        )
      : value;
    return convertedValue;
  }

  getProprietaryFertilizerFormValue(proprietaryFertilizer: CropAid) {
    return {
      ...proprietaryFertilizer,
      indicativePrice: proprietaryFertilizer.indicativePrice / 100,
      fertilizer: proprietaryFertilizer.fertilizer
        ? this.getFertilizerFormValue(
            proprietaryFertilizer.fertilizer,
            proprietaryFertilizer.unitOfMeasurement,
          )
        : null,
    };
  }

  getFertilizerFormValue(fertilizer: FertilizerData, unitOfMeasurement: UnitOfMeasurementEnum) {
    const nutritionalComposition =
      fertilizer.nutritionalComposition ?? this.createEmptyNutritionalCompositionData();

    return {
      ...fertilizer,
      nutritionalComposition: Object.keys(nutritionalComposition).reduce(
        (formNutritionalComposition, nutrient: keyof NutritionalCompositionData) => ({
          ...formNutritionalComposition,
          [nutrient]: this.getNutritionalCompositionFormValue(
            nutritionalComposition[nutrient],
            unitOfMeasurement,
            fertilizer.density,
          ),
        }),
        this.createEmptyNutritionalCompositionData(),
      ),
    };
  }

  getNutritionalCompositionFormValue(
    value: number,
    unitOfMeasurement: UnitOfMeasurementEnum,
    density: number,
  ) {
    return value
      ? (value / this.getUnitOfMeasurementMultiplier(unitOfMeasurement, density)) * 100
      : value;
  }
}
